/**
 * Die native Seite des Anrufs von Vespera (Ticket 2728, Stufe 2):
 *   1. das VoIP-Token der iPhone-App beim Server melden (`verbindeVoipToken`)
 *   2. die Zeilen des nativen Anrufs (CallKit/PushKit) ins Server-Protokoll
 *      reichen (`verbindeAnrufSpur`) — sonst wäre der Gerätetest blind.
 *   3. Auflegen im Anruf-Bildschirm wie mit dem eigenen Knopf (`verbindeAnrufAuflegen`).
 *
 * Abheben selbst läuft NICHT hier: die App reicht beim Abheben den Link
 * `pawbot://anruf?callId=…` an die Seite (derselbe Weg wie Stufe 1,
 * lib/anruf-link.ts), damit es genau EINEN Besitzer des Gesprächs gibt.
 *
 * Zu 1.:
 *
 * Damit Vespera „wie ein echter Anruf“ klingelt (CallKit, auch gesperrt),
 * braucht der Server das VoIP-Token (PushKit) des Geräts. Es entsteht nativ
 * (PawbotAnruf.swift); die Seite holt es ab und schickt es mit ihrer Sitzung an
 * `/api/anruf/voip-token` — derselbe Weg wie das normale Push-Token
 * (wireNativePush in app.ts), nur über das Plugin „PawbotVoice“.
 *
 * Zwei Wege, weil der eine allein nicht reicht:
 *   - `voipToken()` beim Laden: das Token ist meist schon da (PushKit meldet
 *     es beim App-Start, oft bevor die Seite lädt).
 *   - Ereignis `voipToken`: ein neues Token, solange die Seite offen ist.
 *
 * ⚠️ Eine ältere App-Version kennt weder Methode noch Ereignis — dann bleibt
 *    alles still, und es klingelt wie bisher als Meldung zum Tippen.
 * ⚠️ Das Token geht nur an den eigenen Server, nie ins Protokoll der Seite.
 */

interface VoipTokenDaten {
  token: string;
  bundle: string;
  umgebung: "sandbox" | "production";
}

export interface VoipPlugin {
  voipToken?: () => Promise<Partial<VoipTokenDaten> | null | undefined>;
  addListener?: (event: string, cb: (data: unknown) => void) => unknown;
}

const TOKEN_FORM = /^[0-9a-f]{64,200}$/;

/** Nur ein vollständiges, wohlgeformtes Token — sonst null. */
export function voipTokenAus(roh: unknown): VoipTokenDaten | null {
  if (!roh || typeof roh !== "object") return null;
  const d = roh as Record<string, unknown>;
  const token = typeof d.token === "string" ? d.token.trim().toLowerCase() : "";
  const bundle = typeof d.bundle === "string" ? d.bundle.trim() : "";
  const umgebung = d.umgebung;
  if (!TOKEN_FORM.test(token) || !bundle) return null;
  if (umgebung !== "sandbox" && umgebung !== "production") return null;
  return { token, bundle, umgebung };
}

/**
 * Verdrahtet Plugin und Server. `senden` liefert true, wenn der Server es
 * angenommen hat. Dasselbe Token wird je Seitenleben nur einmal gesendet.
 * Liefert ein Versprechen, das nach dem ersten Abholversuch erfüllt ist (Tests).
 */
export function verbindeVoipToken(
  plugin: VoipPlugin | null,
  senden: (d: VoipTokenDaten) => Promise<boolean>,
): Promise<void> {
  if (!plugin) return Promise.resolve();
  let gesendet: string | null = null;
  const melde = async (roh: unknown): Promise<void> => {
    const d = voipTokenAus(roh);
    if (!d || d.token === gesendet) return;
    gesendet = d.token;
    try {
      if (!(await senden(d))) gesendet = null;
    } catch {
      gesendet = null; // beim nächsten Ereignis/Start neu versuchen
    }
  };
  try {
    plugin.addListener?.("voipToken", (data) => {
      void melde(data);
    });
  } catch {
    /* ältere App: kein Ereignis */
  }
  if (typeof plugin.voipToken !== "function") return Promise.resolve();
  return plugin
    .voipToken()
    .then((r) => melde(r))
    .catch(() => undefined);
}

/** Form einer Spur-Zeile des nativen Anrufs: nur Wörter, Zahlen, Kürzel. */
const SPUR_FORM = /^[\p{L}\p{N} .:=_/+-]{1,160}$/u;

/**
 * Die Zeilen des nativen Anrufs („[anruf-nativ] klingelt 1a2b3c4d“) ins
 * Protokoll reichen. Ältere App: kein Ereignis, still. Nur Zeilen in der
 * erwarteten Form — nichts Freies aus dem Gerät landet im Journal.
 */
export function verbindeAnrufSpur(
  plugin: VoipPlugin | null,
  melde: (zeile: string) => void,
): void {
  if (!plugin?.addListener) return;
  try {
    plugin.addListener("anrufSpur", (d) => {
      const roh =
        d && typeof d === "object" && "zeile" in d
          ? String((d).zeile)
          : "";
      const zeile = roh.trim().slice(0, 160);
      if (SPUR_FORM.test(zeile)) melde(`[anruf-nativ] ${zeile}`);
    });
  } catch {
    /* ältere App */
  }
}

/**
 * Auflegen im Anruf-Bildschirm (CallKit): die App meldet `anrufAuflegen`, und
 * die Seite legt auf wie mit ihrem eigenen Knopf — sie ist der Besitzer des
 * Gesprächs, nur so steht ihr Zustand danach richtig. (Schläft die Seite,
 * trennt die App nach 3 s selbst.) Ältere App: kein Ereignis, still.
 */
export function verbindeAnrufAuflegen(plugin: VoipPlugin | null, auflegen: () => void): void {
  if (!plugin?.addListener) return;
  try {
    plugin.addListener("anrufAuflegen", () => {
      auflegen();
    });
  } catch {
    /* ältere App */
  }
}
